//@ts-nocheck
import { useState, useMemo, useRef, useEffect } from 'react';
import {
  MagnifyingGlass,
  Funnel,
  DownloadSimple,
  X,
  Warning
} from '@phosphor-icons/react';
import './ReportsResultPanel.css';

// ─── Render a table cell — converts markdown links to <a> tags ──────────────
const MARKDOWN_LINK_RE = /\[([^\]]+)\]\(((?:https?:\/\/|\/)[^)]+)\)/g;

// Strip markdown links from a raw cell value → plain text label
const stripMarkdownLinks = (val) =>
  String(val ?? '').replace(/\[([^\]]+)\]\((?:https?:\/\/|\/)[^)]+\)/g, '$1');

const renderCell = (value) => {
  if (!value || typeof value !== 'string') return value ?? '—';

  // Check if the cell contains at least one markdown link
  if (!value.includes('](')) return value;

  const parts = [];
  let lastIndex = 0;
  let match;

  MARKDOWN_LINK_RE.lastIndex = 0; // reset stateful regex
  while ((match = MARKDOWN_LINK_RE.exec(value)) !== null) {
    const [full, label, href] = match;

    // Push any plain text before this match
    if (match.index > lastIndex) {
      parts.push(value.slice(lastIndex, match.index));
    }

    parts.push(
      <a
        key={href}
        href={href}
        target='_blank'
        rel='noopener noreferrer'
        className='reports-result-panel__link'
      >
        {label}
      </a>
    );

    lastIndex = match.index + full.length;
  }

  // Push remaining plain text after last match
  if (lastIndex < value.length) {
    parts.push(value.slice(lastIndex));
  }

  return parts;
};

// ─── Markdown table parser ───────────────────────────────────────────────────
const parseMarkdownTable = (text) => {
  if (!text || typeof text !== 'string') return null;

  const lines = text
    .split('\n')
    .map((l) => l.trim())
    .filter(Boolean);
  const tableStart = lines.findIndex((l) => l.startsWith('|'));
  if (tableStart === -1) return null;

  const tableLines = [];
  for (let i = tableStart; i < lines.length; i++) {
    if (!lines[i].startsWith('|')) break;
    tableLines.push(lines[i]);
  }

  if (tableLines.length < 3) return null; // header + separator + at least 1 row

  const parseRow = (line) =>
    line
      .split('|')
      .map((c) => c.trim())
      .filter((c) => c.length > 0);

  const headers = parseRow(tableLines[0]);
  // tableLines[1] is the separator (---|---|...)
  const rows = tableLines.slice(2).map(parseRow);

  // Extract pre-table summary text
  const summaryText = lines.slice(0, tableStart).join(' ').trim();

  return { headers, rows, summaryText };
};

// ─── Detect if response is an error / non-tabular ───────────────────────────
const isErrorResponse = (text) => {
  if (!text) return false;
  const lower = text.toLowerCase();
  return (
    lower.includes("i'm sorry") ||
    lower.includes("i don't") ||
    lower.includes('i cannot') ||
    lower.includes('unable to') ||
    lower.includes('could not') ||
    lower.includes('no data') ||
    lower.includes('no results') ||
    lower.includes('clarify') ||
    lower.includes('please provide') ||
    !text.includes('|') // no markdown table = not tabular
  );
};

// ─── CSV export ──────────────────────────────────────────────────────────────
const exportToCSV = (headers, rows, filename = 'report.csv') => {
  //  Strip markdown links → keep only the label text
  const stripMarkdown = (val) =>
    String(val ?? '').replace(/\[([^\]]+)\]\((?:https?:\/\/|\/)[^)]+\)/g, '$1');
  const escape = (val) => `"${stripMarkdown(val).replace(/"/g, '""')}"`;
  const csvContent = [
    headers.map(escape).join(','),
    ...rows.map((row) => row.map(escape).join(','))
  ].join('\n');

  const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
  const url = URL.createObjectURL(blob);
  const link = document.createElement('a');
  link.href = url;
  link.setAttribute('download', filename);
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
  URL.revokeObjectURL(url);
};

// ─── Component ───────────────────────────────────────────────────────────────
const ReportsResultPanel = ({ resultData }) => {
  const [searchQuery, setSearchQuery] = useState('');
  const [activeFilters, setActiveFilters] = useState([]);
  const [showFilterMenu, setShowFilterMenu] = useState(false);
  const [sortConfig, setSortConfig] = useState({ key: null, dir: 'asc' });
  const filterMenuRef = useRef(null); // for positioning the filter menu

  //  Close filter menu when clicking outside
  useEffect(() => {
    if (!showFilterMenu) return;
    const handleClickOutside = (e) => {
      if (filterMenuRef.current && !filterMenuRef.current.contains(e.target)) {
        setShowFilterMenu(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [showFilterMenu]);

  // resultData can be a string (AI full text) or { text, error }
  const rawText =
    typeof resultData === 'string' ? resultData : (resultData?.text ?? '');

  const hasError = isErrorResponse(rawText);
  const parsed = !hasError ? parseMarkdownTable(rawText) : null;

  const { headers = [], rows = [], summaryText = '' } = parsed ?? {};

  // ── Filter chips from column headers ──────────────────────────────────────
  const availableFilters = headers.filter((h) =>
    ['status', 'trip', 'type', 'category'].some((k) =>
      h.toLowerCase().includes(k)
    )
  );

  const removeFilter = (filter) =>
    setActiveFilters((prev) => prev.filter((f) => f !== filter));

  // ── Sort ──────────────────────────────────────────────────────────────────
  const handleSort = (colIndex) => {
    setSortConfig((prev) =>
      prev.key === colIndex
        ? { key: colIndex, dir: prev.dir === 'asc' ? 'desc' : 'asc' }
        : { key: colIndex, dir: 'asc' }
    );
  };

  // ── Search + filter + sort ────────────────────────────────────────────────
  const processedRows = useMemo(() => {
    let result = [...rows];

    if (searchQuery.trim()) {
      const q = searchQuery.toLowerCase();
      result = result.filter((row) =>
        //  Search against plain text (strip markdown before comparing)
        row.some((cell) => stripMarkdownLinks(cell).toLowerCase().includes(q))
      );
    }

    if (activeFilters.length > 0) {
      result = result.filter((row) =>
        activeFilters.every((filter) =>
          // Filter against plain text too
          row.some((cell) =>
            stripMarkdownLinks(cell)
              .toLowerCase()
              .includes(filter.toLowerCase())
          )
        )
      );
    }

    if (sortConfig.key !== null) {
      result.sort((a, b) => {
        // Sort against plain text
        const aVal = stripMarkdownLinks(a[sortConfig.key] ?? '');
        const bVal = stripMarkdownLinks(b[sortConfig.key] ?? '');
        const numA = parseFloat(aVal.replace(/[^0-9.-]/g, ''));
        const numB = parseFloat(bVal.replace(/[^0-9.-]/g, ''));
        const isNum = !isNaN(numA) && !isNaN(numB);
        const cmp = isNum ? numA - numB : aVal.localeCompare(bVal);
        return sortConfig.dir === 'asc' ? cmp : -cmp;
      });
    }

    return result;
  }, [rows, searchQuery, activeFilters, sortConfig]);

  if (!rawText) return null;

  // ── Error / non-tabular response ──────────────────────────────────────────
  if (hasError || !parsed) {
    return (
      <div className='reports-result-panel reports-result-panel--error'>
        <Warning
          size={20}
          weight='fill'
          className='reports-result-panel__error-icon'
        />
        <p className='reports-result-panel__error-text'>
          {rawText || 'No results to display.'}
        </p>
      </div>
    );
  }

  const sortIcon = (colIndex) => {
    if (sortConfig.key !== colIndex)
      return <span className='reports-result-panel__sort-icon'>⇅</span>;
    return (
      <span className='reports-result-panel__sort-icon reports-result-panel__sort-icon--active'>
        {sortConfig.dir === 'asc' ? '↑' : '↓'}
      </span>
    );
  };

  return (
    <div className='reports-result-panel'>
      {/* ── AI Summary ── */}
      {summaryText && (
        <div className='reports-result-panel__summary'>
          <span className='reports-result-panel__summary-label'>
            ✦ AI SUMMARY
          </span>
          <p className='reports-result-panel__summary-text'>{summaryText}</p>
        </div>
      )}

      {/* ── Meta bar: source + active filter chips ── */}
      <div className='reports-result-panel__meta'>
        <div className='reports-result-panel__source'>
          <span className='reports-result-panel__source-dot' />
          <span className='reports-result-panel__source-text'>
            {processedRows.length} record{processedRows.length !== 1 ? 's' : ''}
          </span>
        </div>

        {activeFilters.length > 0 && (
          <div className='reports-result-panel__filter-chips'>
            {activeFilters.map((filter) => (
              <span key={filter} className='reports-result-panel__chip'>
                {filter}
                <button
                  onClick={() => removeFilter(filter)}
                  className='reports-result-panel__chip-remove'
                >
                  <X size={12} />
                </button>
              </span>
            ))}
          </div>
        )}
      </div>

      {/* ── Toolbar ── */}
      <div className='reports-result-panel__toolbar'>
        <div className='reports-result-panel__search'>
          <MagnifyingGlass
            size={16}
            className='reports-result-panel__search-icon'
          />
          <input
            type='text'
            placeholder='Search this report...'
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            className='reports-result-panel__search-input'
          />
          {searchQuery && (
            <button
              onClick={() => setSearchQuery('')}
              className='reports-result-panel__search-clear'
            >
              <X size={14} />
            </button>
          )}
        </div>

        <div className='reports-result-panel__toolbar-actions'>
          {/* Filter */}
          {availableFilters?.length > 0 && (
            <div
              className='reports-result-panel__filter-wrapper'
              ref={filterMenuRef}
            >
              <button
                className='reports-result-panel__btn'
                onClick={() => setShowFilterMenu((v) => !v)}
              >
                <Funnel size={15} />
                Filter
                {activeFilters?.length > 0 && (
                  <span className='reports-result-panel__btn-badge'>
                    {activeFilters.length}
                  </span>
                )}
              </button>
              {showFilterMenu && (
                <div className='reports-result-panel__filter-menu'>
                  {availableFilters?.map((col) => {
                    const uniqueVals = [
                      ...new Set(
                        rows
                          .map((r) => r[headers.indexOf(col)])
                          .filter(Boolean)
                          //  strip markdown so filter options show plain text
                          .map(stripMarkdownLinks)
                      )
                    ];
                    return (
                      <div
                        key={col}
                        className='reports-result-panel__filter-group'
                      >
                        <span className='reports-result-panel__filter-group-label'>
                          {col}
                        </span>
                        {uniqueVals.map((val) => (
                          <label
                            key={val}
                            className='reports-result-panel__filter-option'
                          >
                            <input
                              type='checkbox'
                              checked={activeFilters?.includes(val)}
                              onChange={(e) =>
                                setActiveFilters((prev) =>
                                  e.target.checked
                                    ? [...prev, val]
                                    : prev.filter((f) => f !== val)
                                )
                              }
                            />
                            {val}
                          </label>
                        ))}
                      </div>
                    );
                  })}
                </div>
              )}
            </div>
          )}

          {/* Export CSV */}
          <button
            className='reports-result-panel__btn reports-result-panel__btn--primary'
            onClick={() => exportToCSV(headers, processedRows)}
          >
            <DownloadSimple size={15} />
            Export CSV
          </button>
        </div>
      </div>

      {/* ── Table ── */}
      <div className='reports-result-panel__table-wrapper'>
        <table className='reports-result-panel__table'>
          <thead>
            <tr>
              {headers.map((h, i) => (
                <th
                  key={i}
                  className='reports-result-panel__th'
                  onClick={() => handleSort(i)}
                >
                  {h} {sortIcon(i)}
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {processedRows.length > 0 ? (
              processedRows?.map((row, ri) => (
                <tr key={ri} className='reports-result-panel__row'>
                  {headers.map((_, ci) => (
                    <td key={ci} className='reports-result-panel__td'>
                      {renderCell(row[ci])} {/*  was: row[ci] ?? '—' */}
                    </td>
                  ))}
                </tr>
              ))
            ) : (
              <tr>
                <td
                  colSpan={headers.length}
                  className='reports-result-panel__empty'
                >
                  No results match your search or filters.
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>

      {/* ── Footer ── */}
      <div className='reports-result-panel__footer'>
        <span>
          Showing {processedRows.length} of {rows.length} records
        </span>
        <span>Updated just now</span>
      </div>
    </div>
  );
};

export default ReportsResultPanel;
